/**
 * What the public verify pages, the My Account users table and the Home grid
 * say about a person's invitation status.
 *
 * A pending invitation expires 30 days after it was last sent, or after the
 * account was created when none was ever sent (main's owner decision). The API
 * reports `expired` for it whether or not its nightly sweep has stored it yet
 * (pacmny-api PendingAccountExpiry), and the bridge renders that as main's
 * word, Expired (bridgeMap.js accountStatusLabel / portalStatus). Sending the
 * invitation again revives it, so My Account opens the same Resend Invitation
 * modal for Expired as for Pending, and the Home grid keeps an Expired patient
 * gated exactly as a Pending one.
 *
 * Main's `recheckPendingAccount` - ask keyon again just before a verify page
 * finalizes - is not carried: no page of this SPA finalizes an account any
 * more. The verify pages hand off to pacmny-auth, which checks the invitation's
 * clock at the moment the person submits their password and answers an expired
 * one with INVITE_EXPIRED (InvitationClock), so a login can never be created
 * for an invitation that ran out while the form was open.
 */

/** keyon's PendingAccountExpiry::MESSAGE, word for word. */
export const INVITE_EXPIRED = 'This invitation has expired. Please ask your organization to send a new one.';
export const ALREADY_VERIFIED = 'This account has already been verified.';

const normalized = (status) => String(status == null ? '' : status).trim().toLowerCase();

export function isExpiredStatus(status) {
  return normalized(status) === 'expired';
}

/** Not yet verified: Pending, or Expired (which a resend revives). */
export function isUnverifiedStatus(status) {
  const s = normalized(status);
  return s === 'pending' || s === 'expired';
}

/** The label of a not-yet-verified status, for a button that opens the resend. */
export function unverifiedLabel(status) {
  return isExpiredStatus(status) ? 'Expired' : 'Pending';
}

/** The sentence a verify page shows when the account is not Pending. */
export function notPendingMessage(status) {
  return isExpiredStatus(status) ? INVITE_EXPIRED : ALREADY_VERIFIED;
}

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * DataTables render for My Account's account_status column. Pending and
 * Expired are links the page opens the Resend Invitation modal from
 * (.account-status-pending-link); anything else is text. Sort, filter and type
 * get the plain label.
 */
export function accountStatusCell(data, type) {
  const raw = String(data == null ? '' : data).trim();
  const status = raw.toLowerCase();
  const label =
    status === 'pending' ? 'Pending' : status === 'active' ? 'Active' : status === 'expired' ? 'Expired' : raw || '—';
  if (type !== 'display') return label;
  if (status === 'pending' || status === 'expired') {
    return '<a href="#" class="account-status-pending-link">' + label + '</a>';
  }
  return escapeHtml(label);
}
